/*
 * wishlist.js
 * ------------------------------------------------------------
 * Wishlist UI. Storage and syncing live in account-sync.js
 * (window.DealSync); this file only handles the page:
 *  - heart buttons:   [data-wishlist-toggle] (snippets/wishlist-button.liquid)
 *  - header counter:  [data-wishlist-bubble] > [data-wishlist-count]
 *  - wishlist page:   [data-wishlist-page]   (sections/main-wishlist.liquid)
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  if (!window.DealSync) return;

  var store = window.DealSync.wishlist;
  var strings = window.DealSync.strings;
  // The wishlist is for logged-in customers. Guests' hearts stay empty and
  // a click sends them to login (see the click handler).
  var isLoggedIn = window.DealSync.isLoggedIn;

  /* ---------------- heart buttons ---------------- */

  function paintButton(btn) {
    var active = isLoggedIn && store.has(btn.dataset.productHandle);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    var label = active ? strings.remove : strings.add;
    btn.setAttribute('aria-label', label);
    btn.setAttribute('title', label);
    var text = btn.querySelector('[data-wishlist-label]');
    if (text) text.textContent = active ? strings.added : strings.add;
  }

  function paintAll(root) {
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(paintButton);
    var count = isLoggedIn ? store.count() : 0;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
    });
    document.querySelectorAll('[data-wishlist-bubble]').forEach(function (el) {
      el.classList.toggle('is-visible', count > 0);
    });
  }

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    // Hearts sit inside product cards that are links - don't navigate.
    evt.preventDefault();
    evt.stopPropagation();

    if (!isLoggedIn) {
      if (window.Shopify && Shopify.designMode) return;
      // Keep the product in this browser; account-sync.js moves it into the
      // account on the first page view after login.
      store.add({
        handle: btn.dataset.productHandle,
        product_id: btn.dataset.productId,
        variant_id: btn.dataset.variantId,
      });
      window.location.href = window.DealSync.loginUrl(window.location.pathname + window.location.search);
      return;
    }

    var added = store.toggle({
      handle: btn.dataset.productHandle,
      product_id: btn.dataset.productId,
      variant_id: btn.dataset.variantId,
    });
    btn.classList.remove('is-animating');
    void btn.offsetWidth;
    btn.classList.add('is-animating');
    toast(added ? strings.added_toast : strings.removed_toast, added);
  }, true);

  document.addEventListener('wishlist:changed', function () {
    paintAll();
  });

  // Cards are added after load by infinite scroll, recommendations, quick
  // view, etc. Paint any new hearts as they appear.
  if ('MutationObserver' in window) {
    var scheduled = false;
    new MutationObserver(function (mutations) {
      if (scheduled) return;
      var relevant = mutations.some(function (m) { return m.addedNodes.length > 0; });
      if (!relevant) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        document.querySelectorAll('[data-wishlist-toggle]:not([aria-pressed])').forEach(paintButton);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ---------------- toast ---------------- */

  var toastEl;
  var toastTimer;

  function toast(message, withLink) {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wishlist-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = '';
    var text = document.createElement('span');
    text.textContent = message;
    toastEl.appendChild(text);
    if (withLink && window.DealSync.wishlistUrl) {
      var link = document.createElement('a');
      link.href = window.DealSync.wishlistUrl;
      link.textContent = strings.view;
      toastEl.appendChild(link);
    }
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 2600);
  }

  /* ---------------- wishlist page ---------------- */

  function initWishlistPage(page) {
    var grid = page.querySelector('[data-wishlist-grid]');
    var empty = page.querySelector('[data-wishlist-empty]');
    var loading = page.querySelector('[data-wishlist-loading]');
    var actions = page.querySelector('[data-wishlist-actions]');
    var clearButton = page.querySelector('[data-wishlist-clear]');
    var rendered = {}; // handle -> <li>
    var loadingNow = {};
    var failed = {};

    function fetchCard(handle) {
      var url = (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') +
        'products/' + encodeURIComponent(handle) + '?view=wishlist-card';
      return fetch(url, { credentials: 'same-origin' }).then(function (res) {
        if (res.status === 404) {
          failed[handle] = 'gone';
          return null;
        }
        if (!res.ok) throw new Error(res.status);
        return res.text();
      });
    }

    function toItem(html) {
      var tpl = document.createElement('template');
      tpl.innerHTML = html.trim();
      return tpl.content.querySelector('li');
    }

    // One skeleton card per product being fetched, up to 8.
    function showSkeleton(count) {
      var cards = loading.children;
      var wanted = Math.max(1, Math.min(8, count));
      if (!cards.length) return;
      while (cards.length < wanted) loading.appendChild(cards[0].cloneNode(true));
      while (cards.length > wanted) loading.removeChild(cards[cards.length - 1]);
      loading.hidden = false;
      page.setAttribute('aria-busy', 'true');
    }

    function hideSkeleton() {
      loading.hidden = true;
      page.setAttribute('aria-busy', 'false');
    }

    // Fade new cards in one after another. The class comes off when the
    // animation ends, so moving a card later (re-sorting) doesn't replay it.
    function animateIn(items) {
      items.forEach(function (li, index) {
        li.style.setProperty('--wishlist-i', Math.min(index, 8));
        li.classList.add('wishlist-card-enter');
        li.addEventListener('animationend', function done(evt) {
          if (evt.target !== li) return;
          li.classList.remove('wishlist-card-enter');
          li.style.removeProperty('--wishlist-i');
          li.removeEventListener('animationend', done);
        });
      });
    }

    function render() {
      var items = store.items();
      var wanted = {};
      items.forEach(function (i) { wanted[i.handle] = true; });

      Object.keys(rendered).forEach(function (handle) {
        if (!wanted[handle]) {
          rendered[handle].remove();
          delete rendered[handle];
        }
      });

      var missing = items.filter(function (i) {
        return !rendered[i.handle] && !failed[i.handle] && !loadingNow[i.handle];
      });
      missing.forEach(function (i) { loadingNow[i.handle] = true; });
      empty.hidden = items.length > 0;
      if (actions) actions.hidden = items.length === 0;
      if (!missing.length) {
        hideSkeleton();
        grid.hidden = items.length === 0;
        return;
      }

      showSkeleton(missing.length);
      var added = [];
      var queue = missing.slice();
      var workers = [];
      // Up to 6 requests at a time.
      for (var w = 0; w < Math.min(6, queue.length); w++) {
        workers.push((function next() {
          var item = queue.shift();
          if (!item) return Promise.resolve();
          return fetchCard(item.handle)
            .then(function (html) {
              if (!html || !store.has(item.handle)) return;
              var li = toItem(html);
              if (li) {
                rendered[item.handle] = li;
                added.push(li);
              }
            })
            .catch(function () {})
            .then(function () {
              delete loadingNow[item.handle];
              return next();
            });
        })());
      }

      Promise.all(workers).then(function () {
        // Products that were deleted from the store are dropped from the list.
        Object.keys(failed).forEach(function (handle) {
          if (failed[handle] === 'gone' && store.has(handle)) store.remove(handle);
        });
        // Keep the wishlist order (newest first).
        var ordered = [];
        store.items().forEach(function (i) {
          if (!rendered[i.handle]) return;
          grid.appendChild(rendered[i.handle]);
          if (added.indexOf(rendered[i.handle]) !== -1) ordered.push(rendered[i.handle]);
        });
        animateIn(ordered);
        hideSkeleton();
        grid.hidden = grid.children.length === 0;
        empty.hidden = store.count() > 0;
        if (actions) actions.hidden = store.count() === 0;
        paintAll(grid);
        // Let the theme bind quick add / quick view on the new cards.
        if (window.theme && theme.QuickAdd) new theme.QuickAdd(grid);
        document.dispatchEvent(new CustomEvent('collection:reloaded'));
      });
    }

    if (clearButton) {
      clearButton.addEventListener('click', function () {
        if (!window.confirm(clearButton.dataset.confirm)) return;
        store.clear();
      });
    }

    render();
    document.addEventListener('wishlist:changed', render);
  }

  /* ---------------- boot ---------------- */

  function init() {
    // Passing through the one-time account sign-in redirect - wait, and
    // only render if the redirect did not happen after all.
    if (window.DealSync.redirecting) {
      document.addEventListener('dealsync:ready', init, { once: true });
      return;
    }
    paintAll();
    document.querySelectorAll('[data-wishlist-page]').forEach(initWishlistPage);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
